import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Terminal, Cpu, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import MetalButton from '../components/MetalButton';
import { buildBreadcrumbSchema, buildFounderSchema, buildOrganizationSchema, toAbsoluteUrl } from '../lib/site';
import './Home.css';

const Home = () => {
    const structuredData = [
        buildOrganizationSchema(),
        buildFounderSchema(),
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'NetHyTech',
            url: toAbsoluteUrl('/'),
            description: 'Jarvis AI assistant, AI products, courses, tutorials, and blogs by NetHyTech and Anubhav Chaturvedi.'
        },
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }])
    ];

    return (
        <div className="home-page">
            <SEO
                title="Jarvis AI Assistant"
                description="Jarvis AI Assistant by NetHyTech helps developers, creators, and operators automate workflows with voice, memory, coding support, and personal AI tooling."
                canonicalPath="/"
                keywords="Jarvis AI, Jarvis AI assistant, personal AI assistant, AI assistant, AI product, AI course, AI blogs, AI automation, NetHyTech, Anubhav Chaturvedi, AI assistant for developers"
                jsonLd={structuredData}
            />

            {/* Hero Section */}
            <section className="hero-section">
                <div className="container">
                    <motion.div
                        className="hero-content"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="hero-badge">
                            <span className="badge-dot"></span>
                            System Online
                        </div>

                        <h1 className="hero-title">
                            <span className="hero-title-line">Jarvis AI Assistant</span>
                            <span className="hero-title-line hero-title-emphasis text-gradient">built for real work.</span>
                        </h1>

                        <p className="hero-subtitle">
                            One focused workspace for daily planning and execution.
                        </p>

                        <div className="hero-capabilities" aria-label="Jarvis AI capabilities">
                            <span className="hero-capability">Voice</span>
                            <span className="hero-capability">Memory</span>
                            <span className="hero-capability">Workflow Execution</span>
                        </div>

                        <div className="hero-actions">
                            <Link to="/products">
                                <MetalButton>
                                    Get Access
                                    <ArrowRight size={18} />
                                </MetalButton>
                            </Link>
                            <Link to="/blog">
                                <button className="btn btn-secondary btn-lg">
                                    Explore Guides
                                </button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="features-section section">
                <div className="container">
                    <div className="features-header">
                        <h2 className="section-title">Why Jarvis AI Assistant Stands Out</h2>
                        <p className="section-subtitle">Engineered for performance, security, and AI-powered productivity.</p>
                    </div>

                    <div className="features-grid">
                        <motion.div
                            className="feature-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                        >
                            <Terminal size={32} className="feature-icon" />
                            <h3>Command Line Interface</h3>
                            <p>Execute complex workflows with natural language commands directly from your terminal.</p>
                        </motion.div>

                        <motion.div
                            className="feature-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                        >
                            <Cpu size={32} className="feature-icon" />
                            <h3>Neural Processing</h3>
                            <p>Advanced local processing capabilities ensuring low latency and high availability.</p>
                        </motion.div>

                        <motion.div
                            className="feature-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                        >
                            <Shield size={32} className="feature-icon" />
                            <h3>Enterprise Security</h3>
                            <p>Military-grade encryption for all data transmission and storage locally.</p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section section">
                <div className="container">
                    <div className="stats-grid">
                        <div className="stat-item">
                            <span className="stat-number">0.1s</span>
                            <span className="stat-label">Response Time</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">100%</span>
                            <span className="stat-label">Privacy Focused</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-number">24/7</span>
                            <span className="stat-label">System Uptime</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
